/**
 * App 列表页
 * 展示所有 App，支持创建、编辑、删除
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Plus,
  Activity,
  Building2,
  Users,
  Crown,
  MoreHorizontal,
  Pencil,
  Trash2,
  LogOut,
  Shield,
  ArrowLeft,
  Loader2,
  Package,
  ImageIcon,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { useAuthStore } from '@/shared/store/authStore'
import { useToast } from '@/shared/hooks/useToast'
import { AppApiService } from '../services/app-api'
import type { AppListItem } from '../types/app'
import AppCreateDialog from '../components/AppCreateDialog'

export default function AppList() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { showSuccess, showError } = useToast()

  // 状态
  const [apps, setApps] = useState<AppListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [appToDelete, setAppToDelete] = useState<AppListItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // 加载 App 列表
  const loadApps = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await AppApiService.getApps()
      if (response.success && response.data) {
        setApps(response.data.apps)
      }
    } catch (error) {
      console.error('❌ [AppList] 加载 App 列表失败:', error)
      showError('加载 App 列表失败，请刷新页面重试')
    } finally {
      setIsLoading(false)
    }
  }, [showError])

  useEffect(() => {
    loadApps()
  }, [loadApps])

  // 删除 App
  const handleDeleteApp = async () => {
    if (!appToDelete) return

    setIsDeleting(true)
    try {
      const response = await AppApiService.deleteApp(appToDelete.app_id)
      if (response.success) {
        showSuccess(`App "${appToDelete.app_name}" 已删除`)
        setApps((prev) => prev.filter((app) => app.app_id !== appToDelete.app_id))
        setAppToDelete(null)
      }
    } catch (error) {
      console.error('❌ [AppList] 删除 App 失败:', error)
      showError('删除 App 失败，请稍后重试')
    } finally {
      setIsDeleting(false)
    }
  }

  // 创建成功回调
  const handleCreateSuccess = (newApp: AppListItem) => {
    setApps((prev) => [newApp, ...prev])
    setIsCreateDialogOpen(false)
    navigate(`/apps/${newApp.app_id}`)
  }

  // 点击 App 卡片
  const handleAppClick = (appId: string) => {
    navigate(`/apps/${appId}`)
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* 顶部导航栏 */}
      <header className="border-b bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Activity className="h-6 w-6 text-primary" />
              </div>
              <span className="font-bold text-xl tracking-tight">Axiom</span>
            </Link>
            <span className="text-slate-300">/</span>
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <span className="font-semibold">App 管理</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回首页
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback>
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 border-b">
                  <p className="text-sm font-medium">{user?.name || '用户'}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                      <Shield className="mr-2 h-4 w-4" />
                      用户管理
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await logout()
                      navigate('/login')
                    } catch (error) {
                      console.error('退出登录失败:', error)
                      navigate('/login')
                    }
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">App 管理</h1>
            <p className="text-slate-500 mt-1">管理你的应用、分类和项目发布。</p>
          </div>
          <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" /> 创建 App
          </Button>
        </div>

        {/* App 卡片网格 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : apps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
              暂无 App
            </h3>
            <p className="text-slate-500 mb-6 max-w-sm">
              创建你的第一个 App，开始管理订阅和项目发布。
            </p>
            <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" /> 创建第一个 App
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <Card
                key={app.app_id}
                className="hover:shadow-lg transition-shadow cursor-pointer group h-full flex flex-col"
                onClick={() => handleAppClick(app.app_id)}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      {app.icon_url ? (
                        <img
                          src={app.icon_url}
                          alt={app.app_name}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-xl group-hover:text-primary transition-colors">
                          {app.app_name}
                        </CardTitle>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {app.company || '未设置公司'}
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => navigate(`/apps/${app.app_id}`)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          编辑
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => setAppToDelete(app)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          删除
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="pb-3 flex-1">
                  <CardDescription className="line-clamp-2 min-h-[40px]">
                    {app.description || '暂无描述'}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-3 border-t bg-slate-50/50 dark:bg-slate-900/50">
                  <div className="flex items-center gap-4 text-sm text-slate-500 w-full">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{app.stats_summary?.total_users ?? 0} 用户</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Crown className="h-4 w-4" />
                      <span>{app.stats_summary?.total_subscribers ?? 0} 订阅</span>
                    </div>
                    <Badge variant="secondary" className="ml-auto">
                      {app.stats_summary?.published_projects ?? 0} 项目
                    </Badge>
                  </div>
                </CardFooter>
              </Card>
            ))}

            {/* 新建 App 占位卡片 */}
            <Button
              variant="outline"
              className="h-full min-h-[200px] border-dashed flex flex-col gap-4 hover:border-primary hover:bg-primary/5"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Plus className="h-6 w-6 text-slate-500" />
              </div>
              <span className="text-lg font-medium text-slate-600">创建新 App</span>
            </Button>
          </div>
        )}
      </main>

      {/* 创建 App 弹窗 */}
      <AppCreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />

      {/* 删除确认弹窗 */}
      <AlertDialog open={!!appToDelete} onOpenChange={(open) => !open && setAppToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除 App</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除 App「{appToDelete?.app_name}」吗？删除后将无法恢复，所有关联的分类和发布配置也会被删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteApp}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                '删除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
